import { useState, useEffect, useMemo, forwardRef } from "react";
import { observer } from "mobx-react";

import { Box } from "@mui/material";

import { PAYERS, STATE_CODE_TO_NAME, LOB_VALUE_TO_NAME } from "../../config";
import { apiService, Payer } from "../../services";
import Column from "../ui/core/Column";
import { useChatCategoryStore } from "../../hooks/useChatCategoryStore";
import { useAppStore } from "../../hooks/useAppStore";
import { PreChatsHelper } from "../helpers/preChatsHelper";
import { ChatCategoryFilters } from "../../stores/ChatCategoryStore";
import { PolicyQuestionsFilters } from "./policy-questions/PolicyQuestionsFilters";
import { PolicyQuestionsTable } from "./policy-questions/PolicyQuestionsTable";
import PolicyWelcomeContent from "./policy-questions/PolicyWelcomeContent";
import ChatInput from "../chat/components/ChatInput";

export interface PolicyFormData {
    payer: string;
    state: string;
    lineOfBusiness: string;
    dateOfService: string;
}

interface PolicyProps {
    onSubmit?: (data: PolicyFormData) => void;
}

export interface PolicyQuestionsRef {
    getFormData: () => PolicyFormData | null;
    isFormComplete: () => boolean;
}

const PolicyQuestions = forwardRef<PolicyQuestionsRef, PolicyProps>(() => {
    const {
        filters: formData,
        setPolicyResearchFilters,
        fetchPolicyResearch,
        setPolicyResearchData,
        clearChatCategoryStore,
        restorePolicyResearchFilters,
        isAskingReveForPolicy,
        setIsAskingReveForPolicy,
        isPoliciesDocumentsDrawerOpen,
        setIsPoliciesDocumentsDrawerOpen,
    } = useChatCategoryStore();
    const { policyToPreview } = useAppStore();

    const [payersData, setPayersData] = useState<Payer[]>([]);
    const [isLoadingPayers, setIsLoadingPayers] = useState(true);

    // Fetch payers with details on component mount
    useEffect(() => {
        const fetchPayers = async () => {
            try {
                setIsLoadingPayers(true);
                const payersResponse = await apiService.getPayers();

                if (payersResponse.success && payersResponse.data) {
                    setPayersData(payersResponse.data);
                } else {
                    console.error('Failed to fetch payers options:', payersResponse.error);
                    // Fallback to hardcoded states with all payers and LOBs
                    const fallbackPayers: Payer[] = Object.entries(STATE_CODE_TO_NAME).map(([code, name]) => ({
                        code: code,
                        name: name,
                        payers: Object.entries(PAYERS).map(([apiName, displayName]) => ({
                            name: apiName,
                            lines_of_business: Object.keys(LOB_VALUE_TO_NAME)
                        }))
                    }));
                    setPayersData(fallbackPayers);
                }
            } catch (error) {
                console.error('Error fetching payers options:', error);
                // Fallback to hardcoded states with all payers and LOBs
                const fallbackPayers: Payer[] = Object.entries(STATE_CODE_TO_NAME).map(([code, name]) => ({
                    code: code,
                    name: name,
                    payers: Object.entries(PAYERS).map(([apiName, displayName]) => ({
                        name: apiName,
                        lines_of_business: Object.keys(LOB_VALUE_TO_NAME)
                    }))
                }));
                setPayersData(fallbackPayers);
            } finally {
                setIsLoadingPayers(false);
            }
        };

        // Start fresh when this mounts, then fetch -- mirrors ContractResearch's mount effect.
        // The user's saved filters (localStorage "filters:policies") are re-applied on top of the reset.
        clearChatCategoryStore(true);
        restorePolicyResearchFilters();
        fetchPayers();
        fetchPolicyResearch();
    }, []);

    // Get selected state data
    const selectedState = useMemo(() => {
        return payersData.find(state => state.name === formData.state || state.code === formData.state);
    }, [payersData, formData.state]);

    const handleChange = (field: keyof ChatCategoryFilters, value: string) => {
        let updatedForm: Partial<ChatCategoryFilters> = {};

        // If state changes, reset dependent fields (payer and LOB)
        if (field === 'state') {
            // Find the state data for the newly selected state
            const newSelectedState = payersData.find(state => state.name === value || state.code === value);
            const newPayersOptions = PreChatsHelper.extractPayerOptions(value, newSelectedState, payersData);

            // If there's only one payer for this state, auto-select it
            if (newPayersOptions.length === 1) {
                const singlePayerName = newPayersOptions[0].value;
                const singlePayer = newSelectedState?.payers?.find(p => p.name === singlePayerName);
                const linesOfBusiness = singlePayer?.lines_of_business || [];

                // If there's also only one line of business, auto-select it too
                if (linesOfBusiness.length === 1) {
                    updatedForm = { state: value, payer: singlePayerName, lineOfBusiness: linesOfBusiness[0] };
                } else {
                    updatedForm = { state: value, payer: singlePayerName, lineOfBusiness: '' };
                }
            } else {
                updatedForm = { state: value, payer: '', lineOfBusiness: '' };
                setPolicyResearchData({ total: 0, policies: [] });
            }
        }
        // If payer changes, reset dependent fields (LOB only)
        else if (field === 'payer') {
            const payer = selectedState?.payers?.find(p => p.name === value);
            const linesOfBusiness = payer?.lines_of_business || [];

            // If there's only one line of business, auto-select it
            if (linesOfBusiness.length === 1) {
                updatedForm = { payer: value, lineOfBusiness: linesOfBusiness[0] };
            } else {
                updatedForm = { payer: value, lineOfBusiness: '' };
            }
        }
        else {
            updatedForm = { [field]: value };
        }

        setPolicyResearchFilters(updatedForm);

        // dateOfService isn't sent to the policy research endpoint (it's only used when
        // submitting a policy question to chat), so no refetch is needed for it.
        if (field !== 'dateOfService') {
            fetchPolicyResearch();
        }
    };

    return (
        <Column
            sx={{
                alignItems: 'stretch',
                justifyContent: 'flex-start',
                width: '100%',
                minWidth: 0,
                height: '100%',
                minHeight: 0,
            }}
        >
            <>
                {/* Keep the filters/table mounted while the Ask Reve welcome flow shows --
                    unmounting would refetch policies on every open/cancel. */}
                <Box sx={{ display: isAskingReveForPolicy ? "none" : "contents" }}>
                    <PolicyQuestionsFilters payersData={payersData} onFormChange={handleChange} isLoadingPayers={isLoadingPayers} />

                    <PolicyQuestionsTable />
                </Box>

                {isAskingReveForPolicy ? (
                    <Column sx={{ height: "100%", width: "100%", minHeight: 0 }}>
                        <PolicyWelcomeContent
                            onBack={() => {
                                setIsAskingReveForPolicy(false);
                                setIsPoliciesDocumentsDrawerOpen(false);
                            }}
                            isPanelOpen={isPoliciesDocumentsDrawerOpen}
                            policyTitle={policyToPreview?.title}
                            onOpenPanel={() => setIsPoliciesDocumentsDrawerOpen(true)}
                        />
                        <ChatInput forceEnable />
                    </Column>
                ) : null}
            </>
        </Column>
    );
});

PolicyQuestions.displayName = 'PolicyQuestions';

export default observer(PolicyQuestions);
